import React, { useEffect, useState } from "react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, FileText } from "lucide-react";

/**
 * Phase 118 — Task Hub "Doc" icon: a slide-out side panel for a row's
 * detailed brief (instructions + links), kept out of the dense row itself
 * so the list stays scannable.
 *
 * Phase 154 — generalized beyond Tasks: the caller supplies `fieldValue`
 * (the current brief text, from `description` on a task or `notes` on a
 * priority/social post) and `onSave` (an async fn that PATCHes the right
 * endpoint and resolves with the updated row), so the same panel works
 * across all three Task Hub tabs without a new data model concept.
 */
export default function TaskBriefPanel({ task, fieldValue, onOpenChange, onSaved, onSave }) {
  const [brief, setBrief] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBrief(fieldValue || ""); }, [fieldValue, task]);

  const save = async () => {
    if (!task) return;
    setSaving(true);
    try {
      const data = await onSave(brief);
      toast.success("Brief saved");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={!!task} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col" data-testid="task-brief-panel">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <FileText size={16} className="text-[#F54800]" /> Brief
          </SheetTitle>
        </SheetHeader>
        <div className="mt-1 text-sm font-medium text-slate-900 truncate" data-testid="task-brief-panel-title">{task?.title}</div>
        <div className="flex-1 mt-4 flex flex-col min-h-0">
          <label className="label-overline mb-1.5">Detailed instructions &amp; links</label>
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Add step-by-step instructions, references, and links (e.g. Figma, Drive, docs)…"
            className="rounded-sm flex-1 min-h-[300px] resize-none text-sm"
            data-testid="task-brief-textarea"
            autoFocus
          />
        </div>
        <SheetFooter className="mt-4 gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="task-brief-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="task-brief-save">
            {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Save brief
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
